"use client";

import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useCopyLang } from "@/components/layout/use-site-ui-copy";
import {
  CC_CURSOR_READ_PILL_TRANSITION_MS,
  CC_CURSOR_READ_SELECTOR,
  getCursorReadLabel,
  isCursorReadEnvironmentSupported,
  setCursorReadActiveRootClass,
} from "@/lib/cc-cursor-read";

/** Place pointer tip at the pill hotspot (bottom-right); see `_cursor-read-pill.scss`. */
function movePill(pill: HTMLDivElement, clientX: number, clientY: number) {
  pill.style.left = `${clientX}px`;
  pill.style.top = `${clientY}px`;
}

export function CcCursorReadPill() {
  const locale = useCopyLang();
  const label = getCursorReadLabel(locale);
  const pillRef = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const root = document.documentElement;

    const sync = () => {
      const next = isCursorReadEnvironmentSupported(finePointer, reducedMotion);
      setEnabled(next);
      root.classList.toggle("cc-cursor-read-enabled", next);
    };

    sync();
    finePointer.addEventListener("change", sync);
    reducedMotion.addEventListener("change", sync);

    return () => {
      finePointer.removeEventListener("change", sync);
      reducedMotion.removeEventListener("change", sync);
      root.classList.remove("cc-cursor-read-enabled");
      setCursorReadActiveRootClass(false);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const pill = pillRef.current;
    if (!pill) return;

    let activeTarget: Element | null = null;
    let deactivateTimer: ReturnType<typeof setTimeout> | null = null;

    const clearDeactivateTimer = () => {
      if (deactivateTimer === null) return;
      clearTimeout(deactivateTimer);
      deactivateTimer = null;
    };

    const scheduleDeactivateRootClass = () => {
      clearDeactivateTimer();
      deactivateTimer = setTimeout(() => {
        deactivateTimer = null;
        setCursorReadActiveRootClass(false);
      }, CC_CURSOR_READ_PILL_TRANSITION_MS);
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!activeTarget) return;
      movePill(pill, event.clientX, event.clientY);
    };

    const onMouseOver = (event: MouseEvent) => {
      const target = (event.target as Element | null)?.closest(CC_CURSOR_READ_SELECTOR);
      if (!target) return;

      clearDeactivateTimer();
      activeTarget = target;
      setCursorReadActiveRootClass(true);
      setVisible(true);
      movePill(pill, event.clientX, event.clientY);
    };

    const onMouseOut = (event: MouseEvent) => {
      if (!activeTarget) return;

      const from = event.target as Element | null;
      if (!from || !activeTarget.contains(from)) return;

      const related = event.relatedTarget as Element | null;
      if (related && activeTarget.contains(related)) return;

      activeTarget = null;
      setVisible(false);
      scheduleDeactivateRootClass();
    };

    window.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseover", onMouseOver);
    document.addEventListener("mouseout", onMouseOut);

    return () => {
      clearDeactivateTimer();
      window.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseover", onMouseOver);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      ref={pillRef}
      className={`cc-cursor-read-pill${visible ? " cc-cursor-read-pill--visible" : ""}`}
      aria-hidden="true"
    >
      <span className="cc-cursor-read-pill__label">{label}</span>
      <ArrowRight className="cc-cursor-read-pill__icon" size={16} strokeWidth={2} aria-hidden />
    </div>
  );
}
